import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Phone, Mail, DollarSign, Calendar, User, ExternalLink } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import api from '../../services/api';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/apiErrors';

const RetailerProfilePage = () => {
  const { businessId, retailerId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [retailer, setRetailer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL'); // ALL, INVOICES, PAYMENTS

  const fetchRetailerProfile = async () => {
    try {
      const { data } = await api.get(`/part5/retailers/${retailerId}/profile`);
      setRetailer(data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load retailer profile'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRetailerProfile();
  }, [retailerId]);

  const openInGoogleMaps = () => {
    if (retailer?.latitude && retailer?.longitude) {
      const url = `https://www.google.com/maps?q=${retailer.latitude},${retailer.longitude}`;
      window.open(url, '_blank');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'UNPAID': 'bg-red-100 text-red-800 border-red-300',
      'PARTIAL': 'bg-orange-100 text-orange-800 border-orange-300',
      'CLEAR': 'bg-green-100 text-green-800 border-green-300'
    };
    return styles[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-96">
          <div className="text-lg text-gray-600">Loading retailer profile...</div>
        </div>
      </div>
    );
  }

  if (!retailer) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-600">Retailer not found</div>
      </div>
    );
  }

  const filteredHistory = retailer.payment_history?.filter((item) => {
    if (filter === 'ALL') return true;
    if (filter === 'INVOICES') return item.type === 'Invoice';
    if (filter === 'PAYMENTS') return item.type === 'Payment';
    return true;
  }) || [];

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Retailer Profile</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Detailed retailer information and history</p>
        </div>
        <Button onClick={() => navigate(-1)} variant="outline">
          Back
        </Button>
      </div>

      {/* Top Section: Basic Info + Location */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Basic Information */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                {retailer.name}
                <span className={`ml-auto px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(retailer.collection_status)}`}>
                  {retailer.collection_status === 'CLEAR' ? '🟢 Clear' :
                   retailer.collection_status === 'PARTIAL' ? '🟠 Partial' :
                   '🔴 Unpaid'}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 text-gray-700">
                <Phone className="w-4 h-4" />
                <span>{retailer.phone || 'N/A'}</span>
              </div>
              {retailer.email && (
                <div className="flex items-center gap-2 text-gray-700">
                  <Mail className="w-4 h-4" />
                  <span>{retailer.email}</span>
                </div>
              )}
              {retailer.billing_address && (
                <div className="flex items-start gap-2 text-gray-700">
                  <MapPin className="w-4 h-4 mt-1" />
                  <div className="text-sm">
                    {retailer.billing_address.line1 && <div>{retailer.billing_address.line1}</div>}
                    {retailer.billing_address.line2 && <div>{retailer.billing_address.line2}</div>}
                    <div>
                      {retailer.billing_address.city && `${retailer.billing_address.city}, `}
                      {retailer.billing_address.state} {retailer.billing_address.pincode}
                    </div>
                  </div>
                </div>
              )}
              {retailer.latitude && retailer.longitude && (
                <Button onClick={openInGoogleMaps} variant="outline" size="sm" className="mt-2">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open in Google Maps
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Collection Summary */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <DollarSign className="w-5 h-5" />
                Collection Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Billed:</span>
                <span className="font-semibold">₹{retailer.payment_summary?.total_billed?.toLocaleString() || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Paid:</span>
                <span className="font-semibold text-green-600">₹{retailer.payment_summary?.total_paid?.toLocaleString() || 0}</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-sm text-gray-600">Outstanding:</span>
                <span className="font-bold text-red-600 text-lg">₹{retailer.outstanding_amount?.toLocaleString() || 0}</span>
              </div>
              {retailer.payment_summary?.last_payment_date && (
                <div className="flex items-center gap-2 text-xs text-gray-500 pt-2">
                  <Calendar className="w-3 h-3" />
                  Last payment: {new Date(retailer.payment_summary.last_payment_date).toLocaleDateString()}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Assigned Staff */}
      {retailer.assigned_staff && retailer.assigned_staff.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Assigned Staff</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {retailer.assigned_staff.map((staff) => (
                <div key={staff.id} className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                  <div className="font-medium">{staff.name}</div>
                  <div className="text-xs text-gray-600">{staff.phone}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Payment History</CardTitle>
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              onClick={() => setFilter('ALL')}
              className={`px-3 py-1.5 text-sm rounded-full font-medium ${
                filter === 'ALL' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('INVOICES')}
              className={`px-3 py-1.5 text-sm rounded-full font-medium ${
                filter === 'INVOICES' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Invoices
            </button>
            <button
              onClick={() => setFilter('PAYMENTS')}
              className={`px-3 py-1.5 text-sm rounded-full font-medium ${
                filter === 'PAYMENTS' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Payments
            </button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2">Date</th>
                  <th className="text-left py-2 px-2">Type</th>
                  <th className="text-left py-2 px-2">Reference</th>
                  <th className="text-right py-2 px-2">Amount</th>
                  <th className="text-left py-2 px-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-8 text-gray-500">
                      No payment history found
                    </td>
                  </tr>
                ) : (
                  filteredHistory.map((item, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-2">
                        {item.date ? new Date(item.date).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="py-2 px-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          item.type === 'Invoice' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {item.type}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-gray-600">{item.reference || 'N/A'}</td>
                      <td className="py-2 px-2 text-right font-medium">₹{item.amount?.toLocaleString() || 0}</td>
                      <td className="py-2 px-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          item.status === 'PAID' ? 'bg-green-100 text-green-800' :
                          item.status === 'PARTIAL' ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Recent Visits */}
      {retailer.recent_visits && retailer.recent_visits.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Visits</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {retailer.recent_visits.map((visit) => (
                <div key={visit.id} className="p-3 border rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium">{visit.staff_name || 'Unknown Staff'}</div>
                      <div className="text-sm text-gray-600">
                        {visit.checkin_time && new Date(visit.checkin_time).toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Purpose: {visit.purpose || 'N/A'}
                      </div>
                    </div>
                    {visit.amount_collected > 0 && (
                      <div className="text-green-600 font-semibold">
                        ₹{visit.amount_collected.toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RetailerProfilePage;
